import { useQuery } from '@tanstack/react-query';
import { FileText, ScrollText, Bell, Smartphone, ShieldCheck, AlertTriangle, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { rerrApi, ifpsApi } from '@/services/api.js';
import { useAuth } from '@/context/AuthContext.jsx';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import { fmtRelative, fmtPriorityIndex } from '@/utils/format.js';

export default function CitizenHome() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const reportsQuery = useQuery({
    queryKey: ['rerr', 'mine'],
    queryFn:  () => rerrApi.myReports({ per_page: 5 }),
    retry:    1,
  });

  const challengesQuery = useQuery({
    queryKey: ['ifps', 'mine'],
    queryFn:  () => ifpsApi.myChallenges({ per_page: 5 }),
    retry:    1,
  });

  const reports    = reportsQuery.data?.data ?? [];
  // Challenges come back as { count, challenges }; reports above are paginated.
  // Reading `.data` here pinned the "waiting on you" card to 0 permanently.
  const challenges = challengesQuery.data?.challenges ?? challengesQuery.data?.data ?? [];
  const openChallengeCount = challenges.filter(c => c.status === 'pending' || c.status === 'open').length;
  const openReportCount    = reports.filter(r => !['closed','failed'].includes(r.status)).length;

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {user?.name ? t('citizen.home.welcomeNamed', { name: user.name.split(' ')[0] }) : `${t('citizen.home.welcome')}.`}
        </h1>
        <p className="mt-1.5 text-sm text-slate-600">
          {t('citizen.home.subtitle')}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard label={t('citizen.home.stats.filed.label')}   value={reportsQuery.isLoading ? '…' : reports.length}        hint={t('citizen.home.stats.filed.hint')}   accent="sky" />
        <StatCard label={t('citizen.home.stats.open.label')}    value={reportsQuery.isLoading ? '…' : openReportCount}       hint={t('citizen.home.stats.open.hint')}    accent="amber" />
        <StatCard label={t('citizen.home.stats.waiting.label')} value={challengesQuery.isLoading ? '…' : openChallengeCount} hint={t('citizen.home.stats.waiting.hint')} accent="rose" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 mb-10">
        <QuickAction
          to="/citizen/submit"
          title={t('citizen.home.actions.submit.title')}
          description={t('citizen.home.actions.submit.desc')}
          icon={FileText}
        />
        <QuickAction
          to="/citizen/bolos"
          title={t('citizen.home.actions.bolos.title')}
          description={t('citizen.home.actions.bolos.desc')}
          icon={AlertTriangle}
        />
        <QuickAction
          to="/citizen/cameras"
          title={t('citizen.home.actions.cameras.title')}
          description={t('citizen.home.actions.cameras.desc')}
          icon={Camera}
        />
        <QuickAction
          to="/citizen/challenges"
          title={t('citizen.home.actions.challenges.title')}
          description={t('citizen.home.actions.challenges.desc')}
          icon={Bell}
        />
        <QuickAction
          to="/citizen/ifps-enrollment"
          title={t('citizen.home.actions.ifps.title')}
          description={t('citizen.home.actions.ifps.desc')}
          icon={ShieldCheck}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('citizen.home.recentReports.title')} description={t('citizen.home.recentReports.description')} />
          <div className="mt-5">
            {reportsQuery.isLoading ? (
              <SkeletonList rows={4} />
            ) : reportsQuery.isError ? (
              <ErrorPanel error={reportsQuery.error} onRetry={reportsQuery.refetch} />
            ) : reports.length === 0 ? (
              <EmptyState title={t('citizen.home.recentReports.empty')} description={t('citizen.home.recentReports.emptyHint')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {reports.map(r => (
                  <li key={r.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {r.event_class?.replace(/_/g, ' ') || 'Report'} · <span className="text-slate-500">{r.trace_id}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {fmtRelative(r.received_at || r.created_at)} · PI {fmtPriorityIndex(r.priority_index)}
                      </div>
                    </div>
                    <StatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={t('citizen.home.recentChallenges.title')}
            description={t('citizen.home.recentChallenges.description')}
          />
          <div className="mt-5">
            {challengesQuery.isLoading ? (
              <SkeletonList rows={4} />
            ) : challengesQuery.isError ? (
              <ErrorPanel error={challengesQuery.error} onRetry={challengesQuery.refetch} />
            ) : challenges.length === 0 ? (
              <EmptyState title={t('citizen.home.recentChallenges.empty')} description={t('citizen.home.recentChallenges.emptyHint')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {challenges.map(c => (
                  <li key={c.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {c.signal_source || c.partner_name || 'Partner'} · <span className="text-slate-500">{c.challenge_type}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {fmtRelative(c.issued_at || c.created_at)}
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 h-5 w-5 text-slate-700" />
          <div>
            <div className="text-sm font-semibold text-slate-900">{t('citizen.home.mobileCallout.title')}</div>
            <p className="mt-1 text-sm text-slate-600">
              {t('citizen.home.mobileCallout.body')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
